/**
 * /admin/rate-limits — admin-only operational view of API-key usage
 * in the current sliding hour. Read-only; no mutations.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminOpsAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Gauge, RefreshCw, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function AdminRateLimits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminOpsAPI.rateLimits();
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load rate limits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (user && user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="admin-denied">
        <ShieldAlert className="w-12 h-12 mx-auto text-destructive mb-3" />
        <h1 className="text-xl font-bold mb-2">Admin only</h1>
        <p className="text-muted-foreground mb-4">
          This page is restricted to platform administrators.
        </p>
        <Button onClick={() => navigate('/')} data-testid="admin-back-home-btn">Back to dashboard</Button>
      </div>
    );
  }

  const keys = data?.keys || [];
  const limit = data?.rate_limit || 100;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-rate-limits-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Gauge className="w-6 h-6 text-primary" /> API Rate Limits
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live snapshot of the sliding-hour usage window — {limit} requests/hour per key.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-rate-limits-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Card className="p-0 overflow-hidden" data-testid="rate-limits-table-card">
        <div className="cp-table-wrapper">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Key Prefix</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Scopes</th>
                <th className="px-4 py-3 font-medium text-right">Usage (1h)</th>
                <th className="px-4 py-3 font-medium text-right">Remaining</th>
                <th className="px-4 py-3 font-medium">Last Used</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td></tr>
              ) : keys.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-muted-foreground">
                  No API keys in this tenant.
                </td></tr>
              ) : keys.map((k) => {
                const pct = Math.min(100, (k.current_hour_usage / limit) * 100);
                const warn = pct >= 80;
                const critical = pct >= 95;
                return (
                  <tr key={k.key_prefix} className="hover:bg-muted/30" data-testid={`rate-row-${k.key_prefix}`}>
                    <td className="px-4 py-3 font-mono text-xs">{k.key_prefix}…</td>
                    <td className="px-4 py-3">{k.name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{k.owner_email || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(k.scopes || []).map((s) => (
                          <Badge key={s} variant="outline" className="font-mono text-[10px] uppercase">{s}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${critical ? 'bg-destructive' : warn ? 'bg-amber-500' : 'bg-primary'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className={critical ? 'text-destructive font-semibold' : warn ? 'text-amber-600' : ''}>
                          {k.current_hour_usage}/{limit}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{k.remaining}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(k.last_used_at)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={k.active ? 'default' : 'secondary'} className={k.active ? 'bg-green-600/10 text-green-700 dark:text-green-400 border-green-600/20' : ''}>
                        {k.active ? 'Active' : 'Disabled'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">
        Window: last {Math.round((data?.window_seconds || 3600) / 60)} minutes · In-memory sliding window · Resets on backend restart.
      </p>
    </div>
  );
}
